"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar, ChevronLeft, MessageSquare } from "lucide-react";
import { DocumentManager } from "../planning/DocumentManager";
import { RichTextEditor } from "../planning/RichTextEditor";
import { useArchivalMode } from "../../contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface DeficiencyDetail {
  deficiency_details: {
    control_relevance: string;
    control_id: string[];
    deficiency_type: string;
    description: string | null;
    root_cause: string | null;
    deficiency_identification: string;
    date_identification: string | null;
    repeat_deficiency: string;
  };
  assessment: {
    ["risk factors"]: {
      textResponse: any;
      documents: any[];
    };
    note_lines: string[];
    magnitude: {
      percentage: string;
      impact: string;
    };
    ["material misstatement"]: {
      selectedOption: string;
      misstatement: string[];
    };
    mitigating_control: {
      selectedOption: string;
      control_id: string[];
    };
  };
  conclusion: {
    deficiency_status: string;
    severity: {
      selectedOption: string;
      textResponse: any;
      documents: any[];
    };
  };
  modify_auditplan: {
    risk_assessment: {
      textResponse: any;
      documents: any[];
    };
    NTE_subsproc: {
      textResponse: any;
      documents: any[];
    };
    information: {
      textResponse: any;
      documents: any[];
    };
  };
}

interface DeficiencyDetailViewProps {
  deficiencyId: string;
  onBack: () => void;
}

type SectionKey =
  | "details"
  | "associations"
  | "assessment"
  | "conclusion"
  | "modify";

export const DeficiencyDetailView: React.FC<DeficiencyDetailViewProps> = ({
  deficiencyId,
  onBack,
}) => {
  const { isArchivalMode } = useArchivalMode();
  const [data, setData] = useState<DeficiencyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>("details");
  const [isDescriptionEditorOpen, setIsDescriptionEditorOpen] = useState(false);
  const [isRootCauseEditorOpen, setIsRootCauseEditorOpen] = useState(false);
  const [controlOptions, setControlOptions] = useState<
    { id: string; label: string }[]
  >([]);
  const [controlSearch, setControlSearch] = useState("");
  const [controlOptionsLoading, setControlOptionsLoading] = useState(false);
  const [controlDetails, setControlDetails] = useState<
    Record<
      string,
      {
        description: string;
        workspaces: string[];
      }
    >
  >({});
  const [controlDetailsLoading, setControlDetailsLoading] = useState(false);
  const [associatedRomms, setAssociatedRomms] = useState<
    { rommId: string; description: string; assessment: string }[]
  >([]);
  const [associatedRommsLoading, setAssociatedRommsLoading] = useState(false);
  const [noteLineOptions, setNoteLineOptions] = useState<string[]>([]);
  const [noteLineSearch, setNoteLineSearch] = useState("");
  const [noteLineOptionsLoading, setNoteLineOptionsLoading] = useState(false);
  const [isMitigatingDialogOpen, setIsMitigatingDialogOpen] = useState(false);
  const [rcmControlMeta, setRcmControlMeta] = useState<
    Record<
      string,
      {
        name: string;
        typeOfControl: string;
        nature: string;
      }
    >
  >({});
  
  // Review comment modal: which card's comments are open (questionId + title for modal)
  const [activeReviewComment, setActiveReviewComment] = useState<{
    questionId: string;
    questionText: string;
    workspace?: string;
    subPhase?: string;
  } | null>(null);

  const fileName = useMemo(
    () => `Libraries_Deficiency_${deficiencyId}.json`,
    [deficiencyId],
  );

  const loadDetail = async () => {
    try {
      setLoading(true);
      if (!window.sharePointAPI?.downloadClientJson) {
        throw new Error("sharePointAPI.downloadClientJson not available");
      }
      const res = await window.sharePointAPI.downloadClientJson(fileName);
      if (!res.success || !res.data) {
        throw new Error(res.error || "Failed to load deficiency detail");
      }
      const payload = res.data[deficiencyId] as DeficiencyDetail | undefined;
      if (payload) {
        setData(payload);
      } else {
        // Fallback to empty template if key not found
        setData({
          deficiency_details: {
            control_relevance: "",
            control_id: [],
            deficiency_type: "",
            description: null,
            root_cause: null,
            deficiency_identification: "",
            date_identification: null,
            repeat_deficiency: "",
          },
          assessment: {
            "risk factors": {
              textResponse: null,
              documents: [],
            },
            note_lines: [],
            magnitude: {
              percentage: "",
              impact: "",
            },
            "material misstatement": {
              selectedOption: "",
              misstatement: [],
            },
            mitigating_control: {
              selectedOption: "",
              control_id: [],
            },
          },
          conclusion: {
            deficiency_status: "",
            severity: {
              selectedOption: "",
              textResponse: null,
              documents: [],
            },
          },
          modify_auditplan: {
            risk_assessment: {
              textResponse: null,
              documents: [],
            },
            NTE_subsproc: {
              textResponse: null,
              documents: [],
            },
            information: {
              textResponse: null,
              documents: [],
            },
          },
        });
      }
    } catch (e) {
      console.error("Failed to load deficiency detail:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadControlOptions = async () => {
    try {
      setControlOptionsLoading(true);
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
        const res = await window.sharePointAPI.downloadRcmJson();
        if (res.success && res.data) {
          const data = res.data;
          const librariesRcm = data.libraries_RCM || data.libraries_rcm || {};
          const entries = Object.entries(librariesRcm) as [string, any][];
          const options = entries.map(([controlId, entry]) => {
            // Strictly use the specified fields from Libraries_RCM.json
            const name: string = entry.control_name ?? "";
            const typeOfControl: string = entry.TypeOfControl ?? "";
            const nature: string = entry.Nature ?? "";

            const labelName = name || controlId;

            return {
              id: controlId,
              label: `${controlId} - ${labelName}`,
              name,
              typeOfControl,
              nature,
            };
          });
          setControlOptions(options.map(({ id, label }) => ({ id, label })));
          const meta: Record<string, { name: string; typeOfControl: string; nature: string }> = {};
          options.forEach((opt) => {
            meta[opt.id] = {
              name: opt.name,
              typeOfControl: opt.typeOfControl,
              nature: opt.nature,
            };
          });
          setRcmControlMeta(meta);
        } else {
          setControlOptions([]);
        }
      } else {
        setControlOptions([]);
      }
    } catch (e) {
      console.error("Failed to load control options for deficiency:", e);
      setControlOptions([]);
    } finally {
      setControlOptionsLoading(false);
    }
  };

  const loadControlDetails = async () => {
    if (!data) return;
    const ids = data.deficiency_details.control_id.map((id) => String(id));
    const missing = ids.filter((id) => !controlDetails[id]);
    if (missing.length === 0) return;

    try {
      setControlDetailsLoading(true);
      const updates: Record<string, { description: string; workspaces: string[] }> = {};
      for (const controlId of missing) {
        if (!window.sharePointAPI?.downloadClientJson) continue;
        const fileName = `Libraries_InternalControlResponses_${controlId}.json`;
        try {
          const res = await window.sharePointAPI.downloadClientJson(fileName);
          if (res.success && res.data) {
            const controlData = res.data;
            const details =
              controlData?.InternalControls?.Control?.ControlSummary?.ControlDetails;
            const desc = (details?.ControlDescription as string) || "";
            const ws = details?.WorkspaceLinked;
            const workspaces = Array.isArray(ws)
              ? (ws as string[])
              : ws
              ? [String(ws)]
              : [];
            updates[controlId] = {
              description: desc,
              workspaces,
            };
          }
        } catch (e) {
          console.error("Failed to load control detail for", controlId, e);
        }
      }
      if (Object.keys(updates).length > 0) {
        setControlDetails((prev) => ({ ...prev, ...updates }));
      }
    } finally {
      setControlDetailsLoading(false);
    }
  };

  const loadAssociatedRomms = async () => {
    if (!data) return;
    const controlIds = data.deficiency_details.control_id.map((id) => String(id));
    if (controlIds.length === 0) {
      setAssociatedRomms([]);
      return;
    }

    try {
      setAssociatedRommsLoading(true);
      if (!window.sharePointAPI?.readRommLibrary) {
        console.warn("sharePointAPI.readRommLibrary not available");
        setAssociatedRomms([]);
        return;
      }

      const res = await window.sharePointAPI.readRommLibrary();
      if (!res.success || !res.data) {
        setAssociatedRomms([]);
        return;
      }

      const rommArray: any[] = Array.isArray(res.data.romm_library)
        ? res.data.romm_library
        : Array.isArray(res.data?.romm_library)
        ? res.data.romm_library
        : [];

      const controlSet = new Set(controlIds.map((id) => id.trim()));
      const seenRommIds = new Set<string>();
      const result: { rommId: string; description: string; assessment: string }[] = [];

      for (const romm of rommArray) {
        const rommId = String(romm["romm-id"] || romm.romm_id || "").trim();
        if (!rommId) continue;

        const rommControls = Array.isArray(romm.control_id)
          ? romm.control_id.map((id: any) => String(id).trim())
          : romm.control_id
          ? [String(romm.control_id).trim()]
          : [];

        const hasMatch = rommControls.some((cid: string) => controlSet.has(cid));
        if (!hasMatch || seenRommIds.has(rommId)) continue;
        seenRommIds.add(rommId);

        result.push({
          rommId,
          description: romm.description || romm.romm_description || "",
          assessment: romm.assesment || romm.assessment || "",
        });
      }

      setAssociatedRomms(result);
    } catch (e) {
      console.error("Failed to load associated ROMMs for deficiency:", e);
      setAssociatedRomms([]);
    } finally {
      setAssociatedRommsLoading(false);
    }
  };

  const loadNoteLineOptions = async () => {
    try {
      setNoteLineOptionsLoading(true);
      if (!window.sharePointAPI?.downloadClientJson) {
        throw new Error("sharePointAPI.downloadClientJson not available");
      }
      const res = await window.sharePointAPI.downloadClientJson(
        "FinData_TrialBalance_Mapping.json",
      );
      if (!res.success || !res.data) {
        setNoteLineOptions([]);
        return;
      }

      const setIds = new Set<string>();

      const walk = (node: any) => {
        if (!node) return;
        if (Array.isArray(node)) {
          node.forEach(walk);
          return;
        }
        if (typeof node === "object") {
          for (const [key, value] of Object.entries(node)) {
            if (key === "note_line" && value !== undefined && value !== null) {
              const v = String(value).trim();
              if (v) setIds.add(v);
            } else {
              walk(value);
            }
          }
        }
      };

      walk(res.data);
      setNoteLineOptions(Array.from(setIds).sort());
    } catch (e) {
      console.error(
        "Failed to load note line options from FinData_TrialBalance_Mapping.json:",
        e,
      );
      setNoteLineOptions([]);
    } finally {
      setNoteLineOptionsLoading(false);
    }
  };

  const saveDetail = async () => {
    if (!data) return;
    try {
      setSaving(true);
      if (!window.sharePointAPI?.uploadDeficiencyDetailJson) {
        throw new Error("sharePointAPI.uploadDeficiencyDetailJson not available");
      }
      const payload = { [deficiencyId]: data };
      const res = await window.sharePointAPI.uploadDeficiencyDetailJson(
        deficiencyId,
        payload,
      );
      if (!res.success) {
        throw new Error(res.error || "Failed to save deficiency detail");
      }

      // Sync the four detail fields (and deficiency_type) to Libraries_Deficiency.json
      if (
        window.sharePointAPI?.downloadDeficiencyJson &&
        window.sharePointAPI?.uploadDeficiencyJson
      ) {
        const defRes = await window.sharePointAPI.downloadDeficiencyJson();
        if (defRes?.success && defRes.data) {
          const defData = defRes.data;
          if (!defData.libraries_deficiency) {
            defData.libraries_deficiency = {};
          }
          const entry = defData.libraries_deficiency[deficiencyId];
          if (entry) {
            entry.control_relevance = data.deficiency_details.control_relevance ?? entry.control_relevance;
            entry.control_id = Array.isArray(data.deficiency_details.control_id)
              ? data.deficiency_details.control_id
              : entry.control_id;
            entry.description = data.deficiency_details.description ?? entry.description;
            entry.root_cause = data.deficiency_details.root_cause ?? entry.root_cause;
            entry.deficiency_type = data.deficiency_details.deficiency_type ?? entry.deficiency_type;
          } else {
            defData.libraries_deficiency[deficiencyId] = {
              control_relevance: data.deficiency_details.control_relevance ?? "",
              control_id: Array.isArray(data.deficiency_details.control_id) ? data.deficiency_details.control_id : [],
              deficiency_type: data.deficiency_details.deficiency_type ?? "",
              description: data.deficiency_details.description ?? null,
              root_cause: data.deficiency_details.root_cause ?? null,
              signoff: {
                prep_so: [],
                rev_so: [],
                conc_so: [],
                eqr_so: [],
                partner_so: [],
              },
            };
          }
          const uploadRes = await window.sharePointAPI.uploadDeficiencyJson(defData);
          if (!uploadRes?.success) {
            console.warn("Deficiency detail saved but Libraries_Deficiency.json sync failed:", uploadRes?.error);
          }
        }
      }
    } catch (e) {
      console.error("Failed to save deficiency detail:", e);
      alert(
        `Failed to save deficiency: ${
          e instanceof Error ? e.message : "Unknown error"
        }`,
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadDetail();
    loadControlOptions();
    loadNoteLineOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deficiencyId]);

  useEffect(() => {
    loadControlDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.deficiency_details.control_id.join("|")]);

  useEffect(() => {
    loadAssociatedRomms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.deficiency_details.control_id.join("|")]);

  if (!data) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={onBack}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Deficiencies
        </Button>
        <div className="text-white/70 text-sm">
          {loading ? "Loading deficiency..." : "Deficiency not found."}
        </div>
      </div>
    );
  }

  const { deficiency_details, assessment, conclusion, modify_auditplan } = data;

  const updateDeficiencyDetails = (
    field: keyof DeficiencyDetail["deficiency_details"],
    value: any,
  ) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            deficiency_details: {
              ...prev.deficiency_details,
              [field]: value,
            },
          }
        : prev,
    );
  };

  const updateAssessment = (
    updater: (prev: DeficiencyDetail["assessment"]) => DeficiencyDetail["assessment"],
  ) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            assessment: updater(prev.assessment),
          }
        : prev,
    );
  };

  const updateConclusion = (
    updater: (prev: DeficiencyDetail["conclusion"]) => DeficiencyDetail["conclusion"],
  ) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            conclusion: updater(prev.conclusion),
          }
        : prev,
    );
  };

  const updateModifyAuditPlan = (
    updater: (
      prev: DeficiencyDetail["modify_auditplan"],
    ) => DeficiencyDetail["modify_auditplan"],
  ) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            modify_auditplan: updater(prev.modify_auditplan),
          }
        : prev,
    );
  };

  const renderDetailsSection = () => (
    <Card className="border-white/10 bg-white/5 text-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Deficiency Details</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setActiveReviewComment({
                questionId: `deficiency-${deficiencyId}-details`,
                questionText: `Deficiency Details - ${deficiencyId}`,
                workspace: "Deficiency",
                subPhase: deficiencyId,
              })
            }
            className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
            aria-label="Review Comments"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {deficiencyId}
          </Badge>
          <span className="text-xs text-white/70">
            {deficiency_details.deficiency_type || "Deficiency"}
          </span>
        </div>

        {/* Uneditable creation info */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-white/70">
            Control Relevance
          </div>
          <div className="text-sm text-white/90">
            {deficiency_details.control_relevance || "-"}
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-white/70">Control ID(s)</div>
          </div>

          <div className="rounded-md border border-white/15 bg-black/20">
            <div className="flex items-center gap-2 border-b border-white/10 px-2 py-1.5">
              <Input
                type="text"
                placeholder="Search controls..."
                value={controlSearch}
                onChange={(e) => setControlSearch(e.target.value)}
                className="h-7 bg-black/40 border-0 px-2 text-xs text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={isArchivalMode}
              />
            </div>
            <div className="max-h-32 overflow-y-auto px-2 py-1 space-y-1 text-xs">
              {controlOptionsLoading ? (
                <div className="text-white/60 text-xs py-2">Loading controls...</div>
              ) : (
                controlOptions
                  .filter((opt) => {
                    const q = controlSearch.toLowerCase().trim();
                    if (!q) return true;
                    return (
                      opt.id.toLowerCase().includes(q) ||
                      opt.label.toLowerCase().includes(q)
                    );
                  })
                  .map((opt) => {
                    const selected = deficiency_details.control_id.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          const current = deficiency_details.control_id;
                          const next = selected
                            ? current.filter((id) => id !== opt.id)
                            : [...current, opt.id];
                          updateDeficiencyDetails("control_id", next);
                        }}
                        className={`w-full rounded px-2 py-1 text-left hover:bg-white/10 ${
                          selected ? "bg-white/10 text-white" : "text-white/80"
                        }`}
                        disabled={isArchivalMode}
                      >
                        <span className="block truncate">{opt.label}</span>
                      </button>
                    );
                  })
              )}
            </div>

            {deficiency_details.control_id.length > 0 && (
              <div className="border-t border-white/10">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/10">
                      <TableHead className="py-2 text-xs text-white/70">
                        Control ID
                      </TableHead>
                      <TableHead className="py-2 text-xs text-white/70">
                        Control Description
                      </TableHead>
                      <TableHead className="py-2 text-xs text-white/70">
                        Workspace(s)
                      </TableHead>
                      <TableHead className="py-2 text-xs text-white/70 text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deficiency_details.control_id.map((cid) => {
                      const info = controlDetails[cid];
                      return (
                        <TableRow key={cid} className="border-b border-white/5">
                          <TableCell className="align-top text-xs">{cid}</TableCell>
                          <TableCell className="align-top text-xs">
                            {info?.description ? (
                              <span className="whitespace-pre-wrap">
                                {info.description}
                              </span>
                            ) : controlDetailsLoading ? (
                              <span className="text-white/40">Loading...</span>
                            ) : (
                              <span className="text-white/40">-</span>
                            )}
                          </TableCell>
                          <TableCell className="align-top text-xs">
                            {info?.workspaces && info.workspaces.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {info.workspaces.map((w) => (
                                  <Badge key={w} variant="outline" className="text-[10px]">
                                    {w}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-white/40">-</span>
                            )}
                          </TableCell>
                          <TableCell className="align-top text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => {
                                const next = deficiency_details.control_id.filter(
                                  (id) => id !== cid,
                                );
                                updateDeficiencyDetails("control_id", next);
                              }}
                              aria-label="Remove control"
                              disabled={isArchivalMode}
                            >
                              ×
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-white/70">
              Description of Deficiency
            </div>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="h-6 px-2 text-[10px] border-white/30 text-white/80 hover:bg-white/10"
              onClick={() => setIsDescriptionEditorOpen(true)}
              disabled={isArchivalMode}
            >
              Edit
            </Button>
          </div>
          <div
            className="prose prose-invert max-w-none text-xs text-white/90 min-h-[40px] border border-white/10 rounded-md p-2 bg-black/20"
            dangerouslySetInnerHTML={{
              __html: deficiency_details.description || "<span class='text-white/40'>Click Edit to add description</span>",
            }}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-white/70">
              Root Cause of Deficiency
            </div>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="h-6 px-2 text-[10px] border-white/30 text-white/80 hover:bg-white/10"
              onClick={() => setIsRootCauseEditorOpen(true)}
              disabled={isArchivalMode}
            >
              Edit
            </Button>
          </div>
          <div
            className="prose prose-invert max-w-none text-xs text-white/90 min-h-[40px] border border-white/10 rounded-md p-2 bg-black/20"
            dangerouslySetInnerHTML={{
              __html: deficiency_details.root_cause || "<span class='text-white/40'>Click Edit to add root cause</span>",
            }}
          />
        </div>

        {/* Editable identification fields */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">
              Who identified the deficiency?
            </div>
            <Select
              value={deficiency_details.deficiency_identification}
              onValueChange={(val) =>
                updateDeficiencyDetails("deficiency_identification", val)
              }
              disabled={isArchivalMode}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Engagement Team">Engagement Team</SelectItem>
                <SelectItem value="Management / Internal Audit">
                  Management (including internal audit)
                </SelectItem>
                <SelectItem value="Others">Others</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">
              Date deficiency was identified
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={deficiency_details.date_identification || ""}
                onChange={(e) =>
                  updateDeficiencyDetails("date_identification", e.target.value || null)
                }
                className="h-8 bg-black/40 border-white/20 text-xs"
                disabled={isArchivalMode}
              />
              <Calendar className="h-4 w-4 text-white/50" />
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <div className="text-xs font-semibold text-white/70">
            Is the deficiency a repeat deficiency from prior year?
          </div>
          <Select
            value={deficiency_details.repeat_deficiency}
            onValueChange={(val) => updateDeficiencyDetails("repeat_deficiency", val)}
            disabled={isArchivalMode}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );

  const renderAssociationsSection = () => (
    <Card className="border-white/10 bg-white/5 text-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Associations</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setActiveReviewComment({
                questionId: `deficiency-${deficiencyId}-associations`,
                questionText: `Associations - ${deficiencyId}`,
                workspace: "Deficiency",
                subPhase: deficiencyId,
              })
            }
            className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
            aria-label="Review Comments"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-white/80">
        <p className="text-xs text-white/60">
          The table below shows all RoMMs which are affected by this deficiency.
        </p>

        {associatedRommsLoading ? (
          <div className="text-xs text-white/60 py-4">Loading associated RoMMs...</div>
        ) : associatedRomms.length === 0 ? (
          <div className="text-xs text-white/60 py-2">
            No associated RoMMs found for the selected control IDs.
          </div>
        ) : (
          <div className="rounded-md border border-white/10 bg-black/30 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/10">
                  <TableHead className="py-2 text-xs text-white/70">
                    RoMM ID
                  </TableHead>
                  <TableHead className="py-2 text-xs text-white/70">
                    Description
                  </TableHead>
                  <TableHead className="py-2 text-xs text-white/70">
                    Assessment
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {associatedRomms.map((romm) => (
                  <TableRow key={romm.rommId} className="border-b border-white/5">
                    <TableCell className="align-top text-xs font-medium text-white">
                      {romm.rommId}
                    </TableCell>
                    <TableCell className="align-top text-xs text-white/90">
                      {romm.description || <span className="text-white/40">-</span>}
                    </TableCell>
                    <TableCell className="align-top text-xs text-white/90">
                      {romm.assessment || <span className="text-white/40">-</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderAssessmentSection = () => (
    <div className="space-y-4">
      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Document risk factors that increase likelihood / magnitude
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-assessment-risk-factors`,
                  questionText: `Risk factors - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`deficiency-${deficiencyId}`}
            groupKey="assessment"
            itemId="risk-factors"
            itemQuestion="Document the risk factors that include a higher likelihood that the deficiency could result in a misstatement (or larger misstatement)."
            itemTextResponse={assessment["risk factors"].textResponse}
            itemDocuments={assessment["risk factors"].documents}
            onDocumentsChange={(docs) =>
              updateAssessment((prev) => ({
                ...prev,
                ["risk factors"]: {
                  ...prev["risk factors"],
                  documents: docs,
                },
              }))
            }
            onTextResponseChange={(textResponse) =>
              updateAssessment((prev) => ({
                ...prev,
                ["risk factors"]: {
                  ...prev["risk factors"],
                  textResponse,
                },
              }))
            }
            disabled={isArchivalMode}
          />
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              What amounts/transactions are exposed to the deficiency?
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-assessment-note-lines`,
                  questionText: `Note lines exposed - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="rounded-md border border-white/15 bg-black/20">
            <div className="flex items-center gap-2 border-b border-white/10 px-2 py-1.5">
              <Input
                type="text"
                placeholder="Search note lines..."
                value={noteLineSearch}
                onChange={(e) => setNoteLineSearch(e.target.value)}
                className="h-7 bg-black/40 border-0 px-2 text-xs text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={isArchivalMode}
              />
            </div>
            <div className="max-h-40 overflow-y-auto px-2 py-1 space-y-1 text-xs">
              {noteLineOptionsLoading ? (
                <div className="text-white/60 text-xs py-2">
                  Loading note lines from FinData_TrialBalance_Mapping.json...
                </div>
              ) : noteLineOptions.length === 0 ? (
                <div className="text-white/60 text-xs py-2">
                  No note lines available in FinData_TrialBalance_Mapping.json.
                </div>
              ) : (
                noteLineOptions
                  .filter((id) => {
                    const q = noteLineSearch.toLowerCase().trim();
                    return !q || id.toLowerCase().includes(q);
                  })
                  .map((id) => {
                    const selected = assessment.note_lines.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          updateAssessment((prev) => {
                            const current = prev.note_lines || [];
                            const next = selected
                              ? current.filter((x) => x !== id)
                              : [...current, id];
                            return { ...prev, note_lines: next };
                          });
                        }}
                        className={`w-full rounded px-2 py-1 text-left hover:bg-white/10 ${
                          selected ? "bg-white/10 text-white" : "text-white/80"
                        }`}
                        disabled={isArchivalMode}
                      >
                        {id}
                      </button>
                    );
                  })
              )}
            </div>
            {assessment.note_lines.length > 0 && (
              <div className="border-t border-white/10 px-2 py-1 flex flex-wrap gap-1">
                {assessment.note_lines.map((id) => (
                    <Badge
                      key={id}
                      variant="outline"
                      className={`text-[10px] ${isArchivalMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={() => {
                        if (isArchivalMode) return;
                        updateAssessment((prev) => ({
                          ...prev,
                          note_lines: prev.note_lines.filter((x) => x !== id),
                        }));
                      }}
                    >
                    {id}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Assess the potential magnitude of the deficiency
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-assessment-magnitude`,
                  questionText: `Magnitude - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">
              What percentage of the population is subjected to the deficiency?
            </div>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={assessment.magnitude.percentage}
              onChange={(e) =>
                updateAssessment((prev) => ({
                  ...prev,
                  magnitude: {
                    ...prev.magnitude,
                    percentage: e.target.value,
                  },
                }))
              }
              className="h-8 bg-black/40 border-white/20 text-xs"
              placeholder="Enter percentage (e.g., 10 for 10%)"
              disabled={isArchivalMode}
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">Assessed impact</div>
            <Input
              type="text"
              value={assessment.magnitude.impact}
              onChange={(e) =>
                updateAssessment((prev) => ({
                  ...prev,
                  magnitude: {
                    ...prev.magnitude,
                    impact: e.target.value,
                  },
                }))
              }
              className="h-8 bg-black/40 border-white/20 text-xs"
              placeholder="Impact amount (to be calculated from note lines × percentage)"
              disabled={isArchivalMode}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Possibility of material misstatement and mitigating controls
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-assessment-misstatement-mitigating`,
                  questionText: `Material misstatement & mitigating controls - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">
              Do we conclude that there is a possibility that the deficiency could
              result in a material misstatement?
            </div>
            <Select
              value={assessment["material misstatement"].selectedOption}
              onValueChange={(val) =>
                updateAssessment((prev) => ({
                  ...prev,
                  ["material misstatement"]: {
                    ...prev["material misstatement"],
                    selectedOption: val,
                  },
                }))
              }
              disabled={isArchivalMode}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {assessment["material misstatement"].selectedOption === "Yes" && (
            <div className="space-y-1">
              <div className="text-xs font-semibold text-white/70">
                Associated misstatements (from misstatement library)
              </div>
              {assessment["material misstatement"].misstatement.length === 0 ? (
                <div className="text-xs text-white/60">
                  No misstatements associated yet. (Linking UI will be implemented
                  against the misstatement library.)
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {assessment["material misstatement"].misstatement.map((m) => (
                    <Badge key={m} variant="outline" className="text-[10px]">
                      {m}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <div className="text-xs font-semibold text-white/70">
              Are there any other controls (redundant or compensating) that mitigate or
              limit the severity of the deficiency?
            </div>
            <Select
              value={assessment.mitigating_control.selectedOption}
              onValueChange={(val) =>
                updateAssessment((prev) => ({
                  ...prev,
                  mitigating_control: {
                    ...prev.mitigating_control,
                    selectedOption: val,
                    // Clear controls if user switches to No
                    control_id: val === "Yes" ? prev.mitigating_control.control_id : [],
                  },
                }))
              }
              disabled={isArchivalMode}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>

            {assessment.mitigating_control.selectedOption === "Yes" && (
              <div className="mt-2 space-y-2">
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  className="h-7 px-2 text-[11px] border-white/30 text-white/80 hover:bg-white/10"
                  onClick={() => setIsMitigatingDialogOpen(true)}
                  disabled={isArchivalMode}
                >
                  Associate controls
                </Button>

                {assessment.mitigating_control.control_id.length > 0 && (
                  <div className="rounded-md border border-white/15 bg-black/10 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-white/10">
                          <TableHead className="py-1.5 text-xs text-white/70">
                            Control ID
                          </TableHead>
                          <TableHead className="py-1.5 text-xs text-white/70">
                            Control Name
                          </TableHead>
                          <TableHead className="py-1.5 text-xs text-white/70">
                            Type of Control
                          </TableHead>
                          <TableHead className="py-1.5 text-xs text-white/70">
                            Nature
                          </TableHead>
                          <TableHead className="py-1.5 text-xs text-white/70 text-right">
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {assessment.mitigating_control.control_id.map((cid) => {
                          const meta = rcmControlMeta[cid];
                          return (
                            <TableRow key={cid} className="border-b border-white/5">
                              <TableCell className="align-top text-xs">{cid}</TableCell>
                              <TableCell className="align-top text-xs">
                                {meta?.name || <span className="text-white/40">-</span>}
                              </TableCell>
                              <TableCell className="align-top text-xs">
                                {meta?.typeOfControl || (
                                  <span className="text-white/40">-</span>
                                )}
                              </TableCell>
                              <TableCell className="align-top text-xs">
                                {meta?.nature || <span className="text-white/40">-</span>}
                              </TableCell>
                              <TableCell className="align-top text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() =>
                                    updateAssessment((prev) => ({
                                      ...prev,
                                      mitigating_control: {
                                        ...prev.mitigating_control,
                                        control_id:
                                          prev.mitigating_control.control_id.filter(
                                            (id) => id !== cid,
                                          ),
                                      },
                                    }))
                                  }
                                  aria-label="Remove control"
                                  disabled={isArchivalMode}
                                >
                                  ×
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderConclusionSection = () => (
    <Card className="border-white/10 bg-white/5 text-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Conclusion</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setActiveReviewComment({
                questionId: `deficiency-${deficiencyId}-conclusion`,
                questionText: `Conclusion - ${deficiencyId}`,
                workspace: "Deficiency",
                subPhase: deficiencyId,
              })
            }
            className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
            aria-label="Review Comments"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-1">
          <div className="text-xs font-semibold text-white/70">
            Deficiency status at year-end
          </div>
          <Select
            value={conclusion.deficiency_status}
            onValueChange={(val) =>
              updateConclusion((prev) => ({
                ...prev,
                deficiency_status: val,
              }))
            }
            disabled={isArchivalMode}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Remediated">Remediated</SelectItem>
              <SelectItem value="Unremediated">Unremediated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <div className="text-xs font-semibold text-white/70">Severity</div>
          <Select
            value={conclusion.severity.selectedOption}
            onValueChange={(val) =>
              updateConclusion((prev) => ({
                ...prev,
                severity: {
                  ...prev.severity,
                  selectedOption: val,
                },
              }))
            }
            disabled={isArchivalMode}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Deficiency">Deficiency</SelectItem>
              <SelectItem value="Significant Deficiency">
                Significant Deficiency
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-white/70">
            Rationale and supporting documentation
          </div>
          <DocumentManager
            sectionKey={`deficiency-${deficiencyId}`}
            groupKey="conclusion"
            itemId="severity"
            itemQuestion="Document the basis for the severity of the deficiency and link supporting workpapers."
            itemTextResponse={conclusion.severity.textResponse}
            itemDocuments={conclusion.severity.documents}
            onDocumentsChange={(docs) =>
              updateConclusion((prev) => ({
                ...prev,
                severity: {
                  ...prev.severity,
                  documents: docs,
                },
              }))
            }
            onTextResponseChange={(textResponse) =>
              updateConclusion((prev) => ({
                ...prev,
                severity: {
                  ...prev.severity,
                  textResponse,
                },
              }))
            }
            disabled={isArchivalMode}
          />
        </div>
      </CardContent>
    </Card>
  );

  const renderModifySection = () => (
    <div className="space-y-4">
      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Effect of deficiency on risk assessment
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-modify-risk`,
                  questionText: `Effect on risk assessment - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`deficiency-${deficiencyId}`}
            groupKey="modify_auditplan"
            itemId="risk_assessment"
            itemQuestion="Consider and document the effect of the deficiency on risk assessment."
            itemTextResponse={modify_auditplan.risk_assessment.textResponse}
            itemDocuments={modify_auditplan.risk_assessment.documents}
            onDocumentsChange={(docs) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                risk_assessment: {
                  ...prev.risk_assessment,
                  documents: docs,
                },
              }))
            }
            onTextResponseChange={(textResponse) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                risk_assessment: {
                  ...prev.risk_assessment,
                  textResponse,
                },
              }))
            }
            disabled={isArchivalMode}
          />
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Effect on nature, timing and extent of substantive procedures
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-modify-nte`,
                  questionText: `Effect on NTE of substantive procedures - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`deficiency-${deficiencyId}`}
            groupKey="modify_auditplan"
            itemId="NTE_subsproc"
            itemQuestion="Consider and document the effect of the deficiency on the nature, timing and extent of substantive procedures."
            itemTextResponse={modify_auditplan.NTE_subsproc.textResponse}
            itemDocuments={modify_auditplan.NTE_subsproc.documents}
            onDocumentsChange={(docs) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                NTE_subsproc: {
                  ...prev.NTE_subsproc,
                  documents: docs,
                },
              }))
            }
            onTextResponseChange={(textResponse) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                NTE_subsproc: {
                  ...prev.NTE_subsproc,
                  textResponse,
                },
              }))
            }
            disabled={isArchivalMode}
          />
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Effect on testing approach of information
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setActiveReviewComment({
                  questionId: `deficiency-${deficiencyId}-modify-information`,
                  questionText: `Effect on information testing - ${deficiencyId}`,
                  workspace: "Deficiency",
                  subPhase: deficiencyId,
                })
              }
              className="flex-shrink-0 p-1 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 rounded transition-colors"
              aria-label="Review Comments"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={`deficiency-${deficiencyId}`}
            groupKey="modify_auditplan"
            itemId="information"
            itemQuestion="Consider and document the effect of the deficiency on the testing approach of information."
            itemTextResponse={modify_auditplan.information.textResponse}
            itemDocuments={modify_auditplan.information.documents}
            onDocumentsChange={(docs) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                information: {
                  ...prev.information,
                  documents: docs,
                },
              }))
            }
            onTextResponseChange={(textResponse) =>
              updateModifyAuditPlan((prev) => ({
                ...prev,
                information: {
                  ...prev.information,
                  textResponse,
                },
              }))
            }
            disabled={isArchivalMode}
          />
        </CardContent>
      </Card>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case "details":
        return renderDetailsSection();
      case "associations":
        return renderAssociationsSection();
      case "assessment":
        return renderAssessmentSection();
      case "conclusion":
        return renderConclusionSection();
      case "modify":
        return renderModifySection();
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full gap-4">
      {/* Left panel with section navigation */}
      <div className="w-56 shrink-0 space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className="flex w-full items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Deficiencies
        </Button>

        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-2">
            <div className="flex flex-col gap-1 text-sm">
              <button
                type="button"
                onClick={() => setActiveSection("details")}
                className={`rounded px-2 py-1 text-left ${
                  activeSection === "details"
                    ? "bg-[#4da3ff]/20 text-white"
                    : "text-white/80 hover:bg-white/10"
                }`}
              >
                Deficiency Details
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("associations")}
                className={`rounded px-2 py-1 text-left ${
                  activeSection === "associations"
                    ? "bg-[#4da3ff]/20 text-white"
                    : "text-white/80 hover:bg-white/10"
                }`}
              >
                Associations
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("assessment")}
                className={`rounded px-2 py-1 text-left ${
                  activeSection === "assessment"
                    ? "bg-[#4da3ff]/20 text-white"
                    : "text-white/80 hover:bg:white/10"
                }`}
              >
                Assessment
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("conclusion")}
                className={`rounded px-2 py-1 text-left ${
                  activeSection === "conclusion"
                    ? "bg-[#4da3ff]/20 text:white"
                    : "text-white/80 hover:bg:white/10"
                }`}
              >
                Conclusion
              </button>
              <button
                type="button"
                onClick={() => setActiveSection("modify")}
                className={`rounded px-2 py-1 text-left ${
                  activeSection === "modify"
                    ? "bg-[#4da3ff]/20 text:white"
                    : "text-white/80 hover:bg:white/10"
                }`}
              >
                Assessment of Modification to Audit Plan
              </button>
            </div>
          </CardContent>
        </Card>

        <Button
          type="button"
          onClick={saveDetail}
          disabled={saving || isArchivalMode}
          className="w-full bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
        >
          {saving ? "Saving..." : "Save Deficiency"}
        </Button>
      </div>

      {/* Main content */}
      <div className="flex-1 space-y-4 overflow-y-auto pr-2">
        {loading && (
          <div className="text-sm text-white/60">Loading deficiency...</div>
        )}
        {renderActiveSection()}

        {/* Description editor */}
        <RichTextEditor
          isOpen={isDescriptionEditorOpen}
          onClose={() => setIsDescriptionEditorOpen(false)}
          content={deficiency_details.description || ""}
          onSave={(content) => {
            updateDeficiencyDetails("description", content || null);
            setIsDescriptionEditorOpen(false);
          }}
        />

        {/* Root cause editor */}
        <RichTextEditor
          isOpen={isRootCauseEditorOpen}
          onClose={() => setIsRootCauseEditorOpen(false)}
          content={deficiency_details.root_cause || ""}
          onSave={(content) => {
            updateDeficiencyDetails("root_cause", content || null);
            setIsRootCauseEditorOpen(false);
          }}
        />

        {/* Mitigating controls association dialog */}
        <Dialog open={isMitigatingDialogOpen} onOpenChange={setIsMitigatingDialogOpen}>
          <DialogContent className="max-w-2xl border-white/20 bg-black/90 text-white">
            <DialogHeader>
              <DialogTitle className="text-white text-base">
                Associate mitigating / compensating controls
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-xs text-white/60">
                Select one or more controls from <code>Libraries_RCM.json</code> to
                associate as mitigating or compensating controls for this deficiency.
              </p>

              <div className="rounded-md border border-white/15 bg-black/40">
                <div className="flex items-center gap-2 border-b border-white/10 px-2 py-1.5">
                  <Input
                    type="text"
                    placeholder="Search controls..."
                    value={controlSearch}
                    onChange={(e) => setControlSearch(e.target.value)}
                    className="h-8 bg-black/40 border-0 px-2 text-xs text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
                    disabled={isArchivalMode}
                  />
                </div>
                <div className="max-h-60 overflow-y-auto px-2 py-1 space-y-1 text-xs">
                  {controlOptionsLoading ? (
                    <div className="text-white/60 text-xs py-2">Loading controls...</div>
                  ) : (
                    controlOptions
                      .filter((opt) => {
                        const q = controlSearch.toLowerCase().trim();
                        if (!q) return true;
                        return (
                          opt.id.toLowerCase().includes(q) ||
                          opt.label.toLowerCase().includes(q)
                        );
                      })
                      .map((opt) => {
                        const selected =
                          assessment.mitigating_control.control_id.includes(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() =>
                              updateAssessment((prev) => {
                                const current = prev.mitigating_control.control_id || [];
                                const next = selected
                                  ? current.filter((id) => id !== opt.id)
                                  : [...current, opt.id];
                                return {
                                  ...prev,
                                  mitigating_control: {
                                    ...prev.mitigating_control,
                                    control_id: next,
                                  },
                                };
                              })
                            }
                            className={`w-full rounded px-2 py-1 text-left hover:bg-white/10 ${
                              selected ? "bg-white/10 text-white" : "text-white/80"
                            }`}
                            disabled={isArchivalMode}
                          >
                            <span className="block truncate">{opt.label}</span>
                          </button>
                        );
                      })
                  )}
                </div>
                {assessment.mitigating_control.control_id.length > 0 && (
                  <div className="border-t border-white/10 px-2 py-1 flex flex-wrap gap-1">
                    {assessment.mitigating_control.control_id.map((cid) => (
                      <Badge
                        key={cid}
                        variant="outline"
                        className="text-[10px] cursor-pointer"
                        onClick={() =>
                          updateAssessment((prev) => ({
                            ...prev,
                            mitigating_control: {
                              ...prev.mitigating_control,
                              control_id: prev.mitigating_control.control_id.filter(
                                (id) => id !== cid,
                              ),
                            },
                          }))
                        }
                      >
                        {cid}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white/80 hover:bg-white/10"
                  onClick={() => setIsMitigatingDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Review Comment Modal - shared; questionId/questionText set by which card opened it */}
        {activeReviewComment && (
          <ReviewComment
            questionId={activeReviewComment.questionId}
            questionText={activeReviewComment.questionText}
            isOpen={true}
            onClose={() => setActiveReviewComment(null)}
            workspace={activeReviewComment.workspace}
            subPhase={activeReviewComment.subPhase}
          />
        )}
      </div>
    </div>
  );
};


